// ─── useSyncedDraft — a composer follows its draft across devices (v425) ────
// On mount (and on every thread switch) the server copy is fetched; while
// mounted, remote changes stream in. Either is applied through ONE rule
// (shouldApplyRemote in draftPersistence.js): never over text the writer
// changed on this device since the last sync, unless the remote is clearly
// newer than that keystroke.
import { useEffect, useRef, useState } from 'react';
import { fetchServerDraft, onDraftChange, setDraftLocal, shouldApplyRemote, lastSyncedText, lastLocalEditAt, onAnyDraftChange, draftIdsFor } from './draftPersistence.js';

export function useSyncedDraft(type, contextId, handlers) {
  const h = useRef(handlers);
  h.current = handlers;
  useEffect(() => {
    if (!contextId) return undefined;
    let alive = true;
    const apply = (remote) => {
      if (!alive || !remote) return;
      const cur = h.current?.getText?.() || '';
      if (!shouldApplyRemote(cur, lastSyncedText(type, contextId), remote.text, { remoteAt: remote.updatedAt, localEditAt: lastLocalEditAt(type, contextId) })) return;
      setDraftLocal(type, contextId, remote.text);
      h.current?.setText?.(remote.text);
    };
    fetchServerDraft(type, contextId).then(apply, () => {});
    const off = onDraftChange(type, contextId, apply);
    return () => { alive = false; off(); };
  }, [type, contextId]);
}

/** The ids of the threads in `scope` that hold an unsent draft (local or on
 *  the server), live. The sidebars mark them with ✎ so a response started on
 *  the other device is easy to find. */
export function useDraftMarks(scope) {
  const [ids, setIds] = useState(() => draftIdsFor(scope));
  useEffect(() => {
    setIds(draftIdsFor(scope));
    return onAnyDraftChange(() => setIds(draftIdsFor(scope)));
  }, [scope]);
  return ids;
}
